/* ==========================================================================
   SPICY HOME - CURSOR
   A hairline ring that trails the pointer, led by the brand's rouge dot.

   Two elements, two transforms, one shared loop. The dot tracks almost
   exactly (0.55 lerp) so the cursor stays precise; the ring follows lazily
   (0.16) so it reads as weight rather than lag. Getting that split wrong is
   what makes custom cursors feel broken -- a single slow follower always
   feels like the page is behind you.

   State is resolved from the pointermove event's own target with closest().
   No elementFromPoint, no rect reads per frame: hit-testing the DOM every
   frame is the other way these end up janky.

   Off on touch, off on reduced motion, off in the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SpicyCursor) return;

  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITOR = !!(window.Shopify && window.Shopify.designMode);

  if (!FINE.matches || REDUCED || EDITOR) return;

  /* ----------------------------------------------------------------------
     What each kind of element says to the pointer.
     Ordered: the first match wins, so the most specific sits at the top.

     A section can override any of this without touching this file:
       data-sh-cursor="media|image|drag|button|link|text|none"
       data-sh-cursor-label="Play"
     ---------------------------------------------------------------------- */
  var RULES = [
    { sel: '[data-sh-cursor]', state: null, label: null },
    { sel: 'input, textarea, select, [contenteditable="true"]', state: 'text', label: '' },
    { sel: '.sh-rail', state: 'drag', label: 'Drag' },
    { sel: 'video[controls], [data-sh-playable]', state: 'media', label: 'Play' },
    { sel: '.sh-card, .sh-chapter__link, .sh-lead', state: 'image', label: 'View' },
    { sel: 'button, .sh-btn, [role="button"], input[type="submit"], label, summary', state: 'button', label: '' },
    { sel: 'a[href], [role="link"]', state: 'link', label: '' }
  ];

  var MAGNETIC = '.sh-btn, [data-sh-magnetic]';
  var MAGNET_MAX = 6;     // px. Any further and it stops feeling like physics.
  var MAGNET_RANGE = 1.4; // multiple of the element's half-size

  var root, ring, dot, label;
  var pointer = { x: -100, y: -100 };
  var ringPos = { x: -100, y: -100 };
  var dotPos = { x: -100, y: -100 };
  var ready = false;

  var magnetEl = null;
  var magnetRect = null;

  /* ----------------------------------------------------------------------
     Build
     ---------------------------------------------------------------------- */
  function build() {
    root = document.createElement('div');
    root.className = 'sh-cursor';
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('data-state', 'default');
    root.innerHTML =
      '<span class="sh-cursor__ring"><span class="sh-cursor__label"></span></span>' +
      '<span class="sh-cursor__dot"></span>';

    document.body.appendChild(root);
    ring = root.querySelector('.sh-cursor__ring');
    dot = root.querySelector('.sh-cursor__dot');
    label = root.querySelector('.sh-cursor__label');

    document.documentElement.classList.add('sh-cursor-on');

    // Buttons become magnetic without any section needing to know.
    var btns = document.querySelectorAll('.sh-btn');
    for (var i = 0; i < btns.length; i++) btns[i].setAttribute('data-sh-magnetic', '');
  }

  /* ----------------------------------------------------------------------
     State resolution - runs on pointermove, from the event target only.
     ---------------------------------------------------------------------- */
  function resolve(target) {
    if (!target || !target.closest) return { state: 'default', label: '' };

    for (var i = 0; i < RULES.length; i++) {
      var hit = target.closest(RULES[i].sel);
      if (!hit) continue;

      if (RULES[i].state === null) {
        // Explicit opt-in wins outright.
        var declared = hit.getAttribute('data-sh-cursor');
        if (declared === 'none') return { state: 'default', label: '' };
        return { state: declared || 'default', label: hit.getAttribute('data-sh-cursor-label') || '' };
      }

      return {
        state: RULES[i].state,
        label: hit.getAttribute('data-sh-cursor-label') || RULES[i].label || ''
      };
    }
    return { state: 'default', label: '' };
  }

  var currentState = 'default';
  var currentLabel = '';
  var currentDark = false;

  function setState(next, text, dark) {
    if (next !== currentState) {
      currentState = next;
      root.setAttribute('data-state', next);
    }
    if (text !== currentLabel) {
      currentLabel = text;
      label.textContent = text;
    }
    if (dark !== currentDark) {
      currentDark = dark;
      root.classList.toggle('is-dark', dark);
    }
  }

  /* ----------------------------------------------------------------------
     Magnetism. The element's rect is measured once when the pointer enters
     it, then reused -- so the pull costs no layout while the pointer moves.
     ---------------------------------------------------------------------- */
  function enterMagnet(el) {
    if (magnetEl === el) return;
    releaseMagnet();
    magnetEl = el;
    magnetRect = el.getBoundingClientRect();
  }

  function releaseMagnet() {
    if (!magnetEl) return;
    magnetEl.style.transform = '';
    magnetEl = null;
    magnetRect = null;
  }

  function applyMagnet() {
    if (!magnetEl || !magnetRect) return null;

    var cx = magnetRect.left + magnetRect.width / 2;
    var cy = magnetRect.top + magnetRect.height / 2;
    var dx = pointer.x - cx;
    var dy = pointer.y - cy;

    var rx = (magnetRect.width / 2) * MAGNET_RANGE;
    var ry = (magnetRect.height / 2) * MAGNET_RANGE;

    if (Math.abs(dx) > rx || Math.abs(dy) > ry) { releaseMagnet(); return null; }

    var px = Math.max(-1, Math.min(1, dx / rx));
    var py = Math.max(-1, Math.min(1, dy / ry));

    magnetEl.style.transform =
      'translate3d(' + (px * MAGNET_MAX).toFixed(2) + 'px,' + (py * MAGNET_MAX).toFixed(2) + 'px,0)';

    // The ring settles toward the centre of what it is about to click.
    return { x: cx + dx * 0.35, y: cy + dy * 0.35 };
  }

  /* ----------------------------------------------------------------------
     Loop - shares GSAP's ticker where one exists, so the site runs a single
     rAF for scrolling, motion and the cursor together.
     ---------------------------------------------------------------------- */
  function frame(dt) {
    var k = Math.min(1, dt / 16.667);
    var anchor = applyMagnet();

    var tx = anchor ? anchor.x : pointer.x;
    var ty = anchor ? anchor.y : pointer.y;

    ringPos.x += (tx - ringPos.x) * (1 - Math.pow(1 - 0.16, k));
    ringPos.y += (ty - ringPos.y) * (1 - Math.pow(1 - 0.16, k));
    dotPos.x += (pointer.x - dotPos.x) * (1 - Math.pow(1 - 0.55, k));
    dotPos.y += (pointer.y - dotPos.y) * (1 - Math.pow(1 - 0.55, k));

    ring.style.transform = 'translate3d(' + ringPos.x.toFixed(2) + 'px,' + ringPos.y.toFixed(2) + 'px,0)';
    dot.style.transform = 'translate3d(' + dotPos.x.toFixed(2) + 'px,' + dotPos.y.toFixed(2) + 'px,0)';
  }

  function startLoop() {
    if (window.gsap && window.gsap.ticker) {
      window.gsap.ticker.add(function (time, delta) { frame(delta || 16.667); });
      return;
    }
    var last = performance.now();
    var tick = function (now) {
      frame(Math.min(48, now - last));
      last = now;
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  }

  /* ----------------------------------------------------------------------
     Events
     ---------------------------------------------------------------------- */
  function onMove(e) {
    pointer.x = e.clientX;
    pointer.y = e.clientY;

    if (!ready) {
      ready = true;
      ringPos.x = dotPos.x = pointer.x;
      ringPos.y = dotPos.y = pointer.y;
      root.classList.add('is-ready');
    }
    root.classList.remove('is-hidden');

    var t = e.target;
    var r = resolve(t);
    var dark = !!(t && t.closest && t.closest('.sh-inverse'));
    setState(r.state, r.label, dark);

    var mag = t && t.closest ? t.closest(MAGNETIC) : null;
    if (mag) enterMagnet(mag); else releaseMagnet();
  }

  function bind() {
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerdown', function () { root.classList.add('is-down'); }, { passive: true });
    document.addEventListener('pointerup', function () { root.classList.remove('is-down'); }, { passive: true });

    document.addEventListener('mouseleave', function () { root.classList.add('is-hidden'); releaseMagnet(); });
    document.addEventListener('mouseenter', function () { root.classList.remove('is-hidden'); });
    window.addEventListener('blur', function () { root.classList.add('is-hidden'); releaseMagnet(); });

    // A magnetised element that scrolls away would keep its stale rect.
    if (window.SpicyScroll) window.SpicyScroll.onScroll(function () { releaseMagnet(); });

    // Sections re-render in the editor and on dynamic updates.
    document.addEventListener('shopify:section:load', function (e) {
      var btns = e.target.querySelectorAll('.sh-btn');
      for (var i = 0; i < btns.length; i++) btns[i].setAttribute('data-sh-magnetic', '');
    });

    // If the pointer turns coarse mid-session, stand down.
    var onChange = function () {
      if (FINE.matches) return;
      document.documentElement.classList.remove('sh-cursor-on');
      root.style.display = 'none';
      releaseMagnet();
    };
    if (FINE.addEventListener) FINE.addEventListener('change', onChange);
    else if (FINE.addListener) FINE.addListener(onChange);
  }

  function init() {
    build();
    bind();
    startLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.SpicyCursor = {
    state: function () { return currentState; },
    release: releaseMagnet
  };
})();
